import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ReadOnlyGuard } from '../permissions/ReadOnlyGuard';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Pencil, Phone, Mail, MapPin, Calendar, User, FileText, Truck, Building, Globe, CheckCircle2, ArrowLeft, CircleDot, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useState } from 'react';
import { useBulkLocks } from '../../hooks/useBulkLocks';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
    nouveau: 'bg-blue-100 text-blue-700 border-blue-200',
    sans_reponse_1: 'bg-orange-100 text-orange-700 border-orange-200',
    sans_reponse_2: 'bg-orange-100 text-orange-700 border-orange-200',
    sans_reponse_3: 'bg-orange-100 text-orange-700 border-orange-200',
    a_rappeler: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    devis_estimatif: 'bg-violet-100 text-violet-700 border-violet-200',
    listing_en_attente: 'bg-slate-100 text-slate-700 border-slate-200',
    listing_telephone: 'bg-violet-100 text-violet-700 border-violet-200',
    listing_email: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    visite: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    faux_contact: 'bg-red-100 text-red-700 border-red-200',
    non_interessant: 'bg-red-100 text-red-600 border-red-200'
};

const STATUS_BORDER_COLORS = {
    nouveau: 'border-blue-400',
    sans_reponse_1: 'border-orange-400',
    sans_reponse_2: 'border-orange-400',
    sans_reponse_3: 'border-orange-400',
    a_rappeler: 'border-yellow-400',
    devis_estimatif: 'border-emerald-400',
    listing_en_attente: 'border-slate-400',
    listing_telephone: 'border-violet-400',
    listing_email: 'border-indigo-400',
    visite: 'border-cyan-400',
    faux_contact: 'border-red-400',
    non_interessant: 'border-red-400'
};

const STATUS_TEXT_COLORS = {
    nouveau: 'text-blue-700',
    sans_reponse_1: 'text-orange-700',
    sans_reponse_2: 'text-orange-700',
    sans_reponse_3: 'text-orange-700',
    a_rappeler: 'text-yellow-700',
    devis_estimatif: 'text-violet-700',
    listing_en_attente: 'text-slate-700',
    listing_telephone: 'text-violet-700',
    listing_email: 'text-indigo-700',
    visite: 'text-cyan-700',
    faux_contact: 'text-red-700',
    non_interessant: 'text-red-600'
};

export function ProspectDetailDialog({ open, onOpenChange, prospect, config, onEdit, onRefresh, onStatusVisite, onRappelRequest, onListingEnAttenteRequest }) {
    const prospectLocks = useBulkLocks('prospect', prospect?.id ? [prospect.id] : [], { enabled: !!prospect?.id && open });
    const lockedByOther = prospect?.id ? prospectLocks[prospect.id] : null;
    const [remarqueModal, setRemarqueModal] = useState(null); // { statut, label }
    const [remarqueText, setRemarqueText] = useState('');
    const [remarqueSaving, setRemarqueSaving] = useState(false);
    if (!prospect) return null;

    const getStatusLabel = (status) => config?.statuses?.find(s => s.value === status)?.label || status;
    const getSourceLabel = (source) => config?.sources?.find(s => s.value === source)?.label || source || '-';
    const getDepartementLabel = (code) => config?.departements?.find(d => d.code === code)?.label || code;
    const getAscenseurLabel = (val) => config?.ascenseur_options?.find(a => a.value === val)?.label || val || '-';

    // Validation helpers
    const isSectionComplete = {
        infos: () => !!prospect.client_nom && !!prospect.telephone && !!prospect.email,
        source: () => !!prospect.source,
        departement: () => !!prospect.departement,
        adresseDepart: () => {
            const a = prospect.adresse_depart;
            return a && !!a.date && !!a.adresse && !!a.etage && !!a.ascenseur && !!a.portage && a.autorisation_stationnement !== null && a.monte_meuble !== null;
        },
        adresseArrivee: () => {
            const a = prospect.adresse_arrivee;
            return a && !!a.date && !!a.adresse && !!a.etage && !!a.ascenseur && !!a.portage && a.autorisation_stationnement !== null && a.monte_meuble !== null;
        },
        remarques: () => !!prospect.remarques
    };

    const getSectionBorderClass = (isComplete) => {
        return '';
    };

    const SectionLine = ({ isComplete }) => (
        <div className={`w-1 rounded-full self-stretch shrink-0 ${isComplete ? 'bg-emerald-500' : 'bg-red-400'}`} />
    );

    const SectionCheck = ({ isComplete }) => (
        isComplete ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> : null
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return format(new Date(dateStr), 'dd MMMM yyyy', { locale: fr });
        } catch {
            return dateStr;
        }
    };

    const STATUS_MESSAGES = {
        sans_reponse_1: 'Statut mis a jour : Relance 1 effectuee',
        sans_reponse_2: 'Statut mis a jour : Relance 2 effectuee',
        sans_reponse_3: 'Statut mis a jour : Relance 3 effectuee',
        a_rappeler: 'Statut mis a jour : Client a rappeler',
        devis_estimatif: 'Statut mis a jour : Devis estimatif — client cree dans inventaire',
        listing_en_attente: 'Statut mis a jour : En attente du listing',
        listing_telephone: 'Statut mis a jour : Listing recu par telephone — client cree dans inventaire',
        listing_email: 'Statut mis a jour : Listing recu par email — client cree dans inventaire',
        visite: 'Statut mis a jour : Visite programmee',
        faux_contact: 'Statut mis a jour : Marque comme faux contact',
        non_interessant: 'Statut mis a jour : Marque comme non interesse',
    };

    const handleStatusChange = async (newStatus) => {
        if (newStatus === 'a_rappeler') {
            // Remonter au parent pour ouvrir le modal rappel en dehors du Dialog Radix
            if (onRappelRequest) {
                onRappelRequest(prospect);
            }
            return;
        }
        if (newStatus === 'listing_en_attente' && onListingEnAttenteRequest) {
            onListingEnAttenteRequest(prospect);
            return;
        }
        // Pour "visite", on ouvre le formulaire SANS changer le statut
        // Le statut ne sera changé que si la visite est confirmée
        if (newStatus === 'visite' && onStatusVisite) {
            onStatusVisite(prospect);
            return;
        }
        // Pour non_interessant / faux_contact : ouverture du modal de remarque obligatoire
        if (newStatus === 'non_interessant' || newStatus === 'faux_contact') {
            const label = getStatusLabel(newStatus);
            setRemarqueModal({ statut: newStatus, label });
            setRemarqueText('');
            return;
        }
        try {
            await axios.patch(
                `${API}/api/prospects/${prospect.id}/status?statut=${newStatus}`,
                {},
                { withCredentials: true }
            );
            toast.success(STATUS_MESSAGES[newStatus] || 'Statut mis à jour');
            onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };

    const submitRemarqueStatut = async () => {
        if (!remarqueText.trim()) {
            toast.error('La remarque est obligatoire');
            return;
        }
        setRemarqueSaving(true);
        try {
            const params = new URLSearchParams({
                statut: remarqueModal.statut,
                remarque: remarqueText.trim(),
            });
            await axios.patch(
                `${API}/api/prospects/${prospect.id}/status?${params.toString()}`,
                {},
                { withCredentials: true },
            );
            toast.success(STATUS_MESSAGES[remarqueModal.statut] || 'Statut mis à jour');
            setRemarqueModal(null);
            setRemarqueText('');
            onRefresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setRemarqueSaving(false);
        }
    };

    const AddressBlock = ({ title, address, icon: Icon, isComplete }) => {
        if (!address || !address.adresse) {
            return (
                <Card className="border-slate-200">
                    <CardHeader className="pb-2 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <Icon size={16} className="text-[#2e7cd6]" />
                            {title}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={false} />
                            <p className="text-sm text-slate-400 italic">Non renseigné</p>
                        </div>
                    </CardContent>
                </Card>
            );
        }
        return (
            <Card className="border-slate-200">
                <CardHeader className="pb-2 border-b border-slate-100">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                        <Icon size={16} className="text-[#2e7cd6]" />
                        {title}
                        <SectionCheck isComplete={isComplete} />
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                    <div className="flex gap-4">
                        <SectionLine isComplete={isComplete} />
                        <div className="flex-1 divide-y divide-slate-100 text-sm">
                            {address.date && (
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Date</p>
                                    <p className="flex items-center gap-2 text-sm font-medium text-slate-900">
                                        <Calendar size={14} className="text-slate-400" />
                                        {formatDate(address.date)}
                                    </p>
                                </div>
                            )}
                            <div className="flex items-center gap-4 py-3">
                                <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Adresse</p>
                                <p className="text-sm font-medium text-slate-900">{address.adresse}</p>
                            </div>
                            {address.etage && (
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Étage</p>
                                    <p className="text-sm font-medium text-slate-900">{address.etage}</p>
                                </div>
                            )}
                            {address.portage && (
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Portage</p>
                                    <p className="text-sm font-medium text-slate-900">{address.portage}</p>
                                </div>
                            )}
                            {address.ascenseur && (
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Ascenseur</p>
                                    <p className="text-sm font-medium text-slate-900">{getAscenseurLabel(address.ascenseur)}</p>
                                </div>
                            )}
                            <div className="flex items-center gap-4 py-3">
                                <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Autorisation stationnement</p>
                                <p className="text-sm font-medium text-slate-900">
                                    {address.autorisation_stationnement ? 'Oui' : 'Non'}
                                </p>
                            </div>
                            <div className="flex items-center gap-4 py-3">
                                <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Monte-meuble</p>
                                <p className="text-sm font-medium text-slate-900">
                                    {address.monte_meuble ? 'Oui' : 'Non'}
                                </p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    return (
    <>
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-5xl w-full max-h-[90dvh] flex flex-col p-0 overflow-hidden"
                data-testid="prospect-detail-dialog"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                hideCloseButton
            >
                <ReadOnlyGuard module="prospects">
                {/* Sticky Header */}
                <div className="sticky top-0 z-50 bg-white border-b px-4 md:px-6 py-3 md:py-4 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onOpenChange(false)}
                            data-testid="back-btn"
                        >
                            <ArrowLeft size={16} className="mr-2" />
                            Retour
                        </Button>
                        <DialogTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Aperçu du prospect
                        </DialogTitle>
                    </div>
                    <Button
                        variant="outline"
                        onClick={onEdit}
                        disabled={!!lockedByOther}
                        title={lockedByOther ? `${lockedByOther.user_name} est en train de modifier cette fiche` : ''}
                        data-testid="edit-prospect-btn"
                    >
                        <Pencil size={16} className="mr-2" />
                        Modifier
                    </Button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
                    <div className="space-y-6">
                    {/* Section 1: Statut */}
                    <Card className={`bg-blue-50 border-2 ${STATUS_BORDER_COLORS[prospect.statut] || 'border-blue-200'}`}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-blue-800">
                                <CircleDot size={16} />
                                1. Statut du prospect
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-4">
                                {prospect.statut === 'visite' ? (
                                    <>
                                        <Badge variant="outline" className={`${STATUS_COLORS[prospect.statut]} border text-sm px-3 py-1`}>
                                            {getStatusLabel(prospect.statut)}
                                        </Badge>
                                        <span className="text-xs text-slate-500 italic" data-testid="status-locked-visite">
                                            Statut verrouillé — la visite est planifiée
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <Select value={prospect.statut} onValueChange={handleStatusChange} disabled={!!lockedByOther}>
                                            <SelectTrigger className="w-[250px] bg-white" data-testid="detail-status-select" title={lockedByOther ? `${lockedByOther.user_name} est en train de modifier cette fiche` : ''}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {config.statuses.filter((s) => {
                                                    const cur = prospect.statut;
                                                    const v = s.value;
                                                    if (v === cur) return true;
                                                    if (cur === 'faux_contact' || cur === 'non_interessant') return false;
                                                    if (cur === 'nouveau') return v !== 'sans_reponse_2' && v !== 'sans_reponse_3';
                                                    if (cur === 'sans_reponse_1') return v !== 'nouveau' && v !== 'sans_reponse_3';
                                                    if (cur === 'sans_reponse_2') return v !== 'nouveau' && v !== 'sans_reponse_1';
                                                    if (cur === 'sans_reponse_3') return v !== 'nouveau' && v !== 'sans_reponse_1' && v !== 'sans_reponse_2';
                                                    if (cur === 'a_rappeler') return v !== 'nouveau' && v !== 'sans_reponse_1' && v !== 'sans_reponse_2' && v !== 'sans_reponse_3';
                                                    if (cur === 'devis_estimatif') return v !== 'nouveau' && v !== 'sans_reponse_1' && v !== 'sans_reponse_2' && v !== 'sans_reponse_3';
                                                    if (cur === 'listing_en_attente') return v === 'listing_telephone' || v === 'listing_email' || v === 'listing_en_attente';
                                                    if (cur === 'listing_telephone' || cur === 'listing_email') return v === 'visite' || v === cur;
                                                    return true;
                                                }).map((s) => (
                                                    <SelectItem key={s.value} value={s.value}>
                                                        <span className={`inline-block px-2 py-0.5 rounded text-sm ${STATUS_COLORS[s.value] || ''}`}>
                                                            {s.label}
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Badge variant="outline" className={`${STATUS_COLORS[prospect.statut]} border text-sm px-3 py-1`}>
                                            {getStatusLabel(prospect.statut)}
                                        </Badge>
                                    </>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section 2: Informations générales */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <User size={16} className="text-[#2e7cd6]" />
                                2. Informations générales
                                <SectionCheck isComplete={isSectionComplete.infos()} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.infos()} />
                                <div className="flex-1 divide-y divide-slate-100">
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Nom</p>
                                        <p className="text-sm font-medium text-slate-900">{prospect.client_nom}</p>
                                    </div>
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Téléphone</p>
                                        {prospect.telephone ? (
                                            <p className="flex items-center gap-2 text-sm text-slate-900">
                                                <Phone size={14} className="text-[#2e7cd6]" />
                                                {prospect.telephone}
                                            </p>
                                        ) : (
                                            <p className="text-slate-400 italic text-sm">Non renseigné</p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Email</p>
                                        {prospect.email ? (
                                            <p className="flex items-center gap-2 text-sm text-slate-900">
                                                <Mail size={14} className="text-[#2e7cd6]" />
                                                {prospect.email}
                                            </p>
                                        ) : (
                                            <p className="text-slate-400 italic text-sm">Non renseigné</p>
                                        )}
                                    </div>
                                    <div className="pt-3">
                                        <p className="text-xs text-slate-500">
                                            Créé le {formatDate(prospect.created_at)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section 3: Source & Département */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <Globe size={16} className="text-[#2e7cd6]" />
                                3. Source & Département
                                <SectionCheck isComplete={isSectionComplete.source() && isSectionComplete.departement()} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.source() && isSectionComplete.departement()} />
                                <div className="flex-1 divide-y divide-slate-100">
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Source</p>
                                        <p className="text-sm font-medium text-slate-900">
                                            {getSourceLabel(prospect.source) || <span className="text-slate-400 italic">Non renseigné</span>}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Département</p>
                                        <p className="text-sm font-medium text-slate-900">
                                            {getDepartementLabel(prospect.departement)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section 4: Adresse de départ */}
                    <AddressBlock
                        title="4. Adresse de départ"
                        address={prospect.adresse_depart}
                        icon={MapPin}
                        isComplete={isSectionComplete.adresseDepart()}
                    />

                    {/* Section 5: Adresse d'arrivée */}
                    <AddressBlock
                        title="5. Adresse d'arrivée"
                        address={prospect.adresse_arrivee}
                        icon={Truck}
                        isComplete={isSectionComplete.adresseArrivee()}
                    />

                    {/* Section 6: Remarques */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <FileText size={16} className="text-[#2e7cd6]" />
                                6. Remarques
                                <SectionCheck isComplete={isSectionComplete.remarques()} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.remarques()} />
                                <div className="flex-1">
                                    <div className="flex items-start gap-4 py-3">
                                        <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0 pt-1">Remarques</p>
                                        {prospect.remarques ? (
                                            <p className="text-sm text-slate-900 whitespace-pre-wrap">{prospect.remarques}</p>
                                        ) : (
                                            <p className="text-slate-400 italic">Aucune remarque</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    </div>
                </div>
                </ReadOnlyGuard>
            </DialogContent>
        </Dialog>

        {/* Modal remarque obligatoire (non_interessant / faux_contact) */}
        <Dialog open={!!remarqueModal} onOpenChange={(o) => { if (!o) { setRemarqueModal(null); setRemarqueText(''); } }}>
            <DialogContent className="max-w-md p-0 z-[10000]" data-testid="prospect-remarque-modal">
                <div className="px-5 py-3 border-b border-slate-200">
                    <DialogTitle className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Passer en « {remarqueModal?.label} »
                    </DialogTitle>
                    <p className="text-xs text-slate-500 mt-1">
                        Une remarque est obligatoire pour expliquer ce changement.
                    </p>
                </div>
                <div className="p-5">
                    <Textarea
                        value={remarqueText}
                        onChange={e => setRemarqueText(e.target.value)}
                        rows={4}
                        placeholder={
                            remarqueModal?.statut === 'faux_contact'
                                ? 'Ex : numéro inexistant, mauvais interlocuteur, doublon...'
                                : 'Ex : projet annulé, prix trop élevé, déménagement reporté...'
                        }
                        className="resize-none"
                        data-testid="prospect-remarque-textarea"
                        autoFocus
                    />
                </div>
                <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
                    <Button variant="outline" size="sm" onClick={() => { setRemarqueModal(null); setRemarqueText(''); }} disabled={remarqueSaving}>
                        Annuler
                    </Button>
                    <Button
                        size="sm"
                        className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                        onClick={submitRemarqueStatut}
                        disabled={remarqueSaving || !remarqueText.trim()}
                        data-testid="prospect-remarque-confirm"
                    >
                        {remarqueSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                        Valider le statut
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    </>
    );
}
